import { useEffect } from "react";
import { useContent, getSeoOverride, getBlock } from "@/lib/content";
import { CONTACT } from "@/data/site";

const SITE_URL = "https://clisecure.com";
const SITE_NAME = "CLI Secure";

function upsertMeta(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel, href) {
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

// Inject (or update / remove) a named <script type="application/ld+json">.
function upsertJsonLd(id, obj) {
  let s = document.getElementById(id);
  if (obj) {
    if (!s) {
      s = document.createElement("script");
      s.id = id;
      s.type = "application/ld+json";
      document.head.appendChild(s);
    }
    s.textContent = JSON.stringify(obj);
  } else if (s) {
    s.remove();
  }
}

// Site-wide LocalBusiness/Organization node, built from Site settings (with
// static fallbacks). Emitted on every page so Google can build a rich profile.
function buildOrganizationLd() {
  const s = getBlock("settings") || {};
  const name = s.brandName || s.companyName || SITE_NAME;
  const phone = s.phone || CONTACT.phone;
  const email = s.email || CONTACT.email;
  const address = s.address || CONTACT.address;
  const social = s.social || {};
  const sameAs = [social.linkedin, social.twitter, social.facebook, social.instagram, social.youtube].filter(Boolean);
  const ld = {
    "@context": "https://schema.org",
    "@type": ["Organization", "LocalBusiness"],
    "@id": `${SITE_URL}/#organization`,
    name,
    url: SITE_URL,
    logo: `${SITE_URL}/logo.png`,
    image: `${SITE_URL}/og-image.png`,
    telephone: phone,
    email,
    address: { "@type": "PostalAddress", addressLocality: "London", addressCountry: "GB", ...(address ? { streetAddress: address } : {}) },
    areaServed: [
      { "@type": "City", name: "Greater London" },
      { "@type": "Country", name: "United Kingdom" },
      { "@type": "Place", name: "Europe" },
    ],
  };
  if (sameAs.length) ld.sameAs = sameAs;
  if (s.companyNo) ld.identifier = s.companyNo;
  return ld;
}

// Site-wide WebSite node — helps Google understand the site name shown in
// search results and ties pages back to the publisher Organization.
function buildWebsiteLd() {
  const s = getBlock("settings") || {};
  const name = s.brandName || s.companyName || SITE_NAME;
  return {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name,
    url: SITE_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
    inLanguage: "en-GB",
  };
}

// Auto BreadcrumbList from the URL path, e.g. /cyber-security → Home › Cyber Security.
const TITLE_CASE_EXC = { it: "IT", edr: "EDR", mdr: "MDR", siem: "SIEM", soc: "SOC", voip: "VoIP", pbx: "PBX", ai: "AI", dmarc: "DMARC", sim: "SIM", crm: "CRM", "sd-wan": "SD-WAN", fttp: "FTTP", did: "DID", "microsoft-365": "Microsoft 365", cctv: "CCTV", us: "Us" };
function humanizeSlug(slug) {
  if (TITLE_CASE_EXC[slug]) return TITLE_CASE_EXC[slug];
  return slug.split("-").map((w) => TITLE_CASE_EXC[w] || (w.charAt(0).toUpperCase() + w.slice(1))).join(" ");
}
function buildBreadcrumbLd(path, lastName) {
  const segs = path.split("/").filter(Boolean);
  if (segs.length === 0) return null; // no breadcrumb on the home page
  const items = [{ "@type": "ListItem", position: 1, name: "Home", item: SITE_URL }];
  let acc = "";
  segs.forEach((seg, i) => {
    acc += `/${seg}`;
    const isLast = i === segs.length - 1;
    items.push({ "@type": "ListItem", position: i + 2, name: isLast && lastName ? lastName : humanizeSlug(seg), item: `${SITE_URL}${acc}` });
  });
  return { "@context": "https://schema.org", "@type": "BreadcrumbList", itemListElement: items };
}

// Category share images for service/product pages. When a page doesn't pass its
// own image (and the admin hasn't set one in the SEO editor), social shares use
// the matching service picture instead of the generic site card.
const DEFAULT_OG = "/og-image.png";
const SERVICE_OG = (() => {
  const g = (paths, img) => paths.reduce((m, p) => ((m[p] = img), m), {});
  return {
    ...g(["/managed-it", "/it-support-london", "/onsite-it-support", "/remote-it-support", "/co-managed-it-support", "/project-delivery"], "/svc-managed-it.png"),
    ...g(["/cyber-security", "/endpoint-security-edr", "/managed-detection-response", "/network-security", "/managed-siem-soc", "/identity-protection", "/privileged-access-management", "/managed-dmarc", "/email-security", "/dark-web-monitoring"], "/svc-cyber-security.png"),
    ...g(["/cloud-microsoft", "/microsoft-365-support", "/microsoft-365-migration", "/sharepoint-consultancy", "/teams-consultancy", "/cloud-backup-solutions"], "/svc-cloud-microsoft.png"),
    ...g(["/network-consultancy", "/cisco-network-consultancy", "/aruba-network-consultancy", "/fortinet-network-consultancy", "/grandstream-network-consultancy", "/wireless-network-consultancy"], "/svc-network-consultancy.png"),
    ...g(["/connectivity-telecoms", "/leased-lines", "/dedicated-internet-access", "/fttp-leased-line", "/sd-wan", "/multi-site-connectivity", "/backup-connectivity", "/business-mobile-sim", "/sim-only-deals", "/mobile-broadband", "/cloud-hosted-pbx", "/sip-trunk", "/teams-phone-system", "/phone-numbers", "/integrations", "/ai-receptionist", "/ai-contact-center"], "/svc-connectivity-telecoms.png"),
    ...g(["/surveillance-data", "/data-cabling", "/cloud-cctv"], "/svc-surveillance-data.png"),
  };
})();

// Search engines cut meta descriptions at ~155-160 characters. Long page
// subtitles are reused as descriptions on many pages, so clamp them at a
// sentence (or word) boundary instead of letting Google truncate mid-word.
// Mirrored in scripts/stamp-seo.mjs and the backend shell.
export function clampDescription(text, max = 158) {
  const s = String(text || "").replace(/\s+/g, " ").trim();
  if (s.length <= 160) return s;
  const sentences = s.match(/[^.!?]+[.!?]+(\s|$)/g) || [];
  let out = "";
  for (const sen of sentences) {
    if ((out + sen).trim().length > max) break;
    out += sen;
  }
  out = out.trim();
  if (out.length >= 70) return out;
  const cut = s.slice(0, max - 1);
  return cut.slice(0, cut.lastIndexOf(" ")).replace(/[\s,;:—–-]+$/, "") + "…";
}

export default function Seo({
  title,
  description,
  path = "/",
  image = DEFAULT_OG,
  type = "website",
  jsonLd = null,
  keywords = "",
  noindex: noindexProp = false,
}) {
  // Re-render when CMS content loads so admin-managed overrides apply.
  const { loaded } = useContent();
  const override = getSeoOverride(path);
  const finalTitle = override.title || title;
  const finalDescription = clampDescription(override.description || description);
  // Priority: admin override → the page's own image → a service-category image
  // for that route → the generic site card.
  const finalImageSrc = override.image || (image && image !== DEFAULT_OG ? image : (SERVICE_OG[path] || image));
  const finalKeywords = override.keywords || keywords || "";
  const noindex = !!override.noindex || noindexProp;

  const url = `${SITE_URL}${path}`;
  const canonical = override.canonical || url;
  const img = finalImageSrc.startsWith("http") ? finalImageSrc : `${SITE_URL}${finalImageSrc}`;
  const jsonLdStr = jsonLd ? JSON.stringify(jsonLd) : "";

  useEffect(() => {
    const title = finalTitle;
    const description = finalDescription;
    document.title = title;
    upsertMeta("name", "description", description);
    void loaded;
    // Search-engine ownership verification tokens (Site settings → SEO tools).
    const settings = getBlock("settings") || {};
    const seoTools = settings.seoTools || {};
    if (seoTools.googleVerification) upsertMeta("name", "google-site-verification", seoTools.googleVerification);
    if (seoTools.bingVerification) upsertMeta("name", "msvalidate.01", seoTools.bingVerification);
    // Admin-set favicon overrides the static logo-chevron in the browser tab.
    if (settings.favicon) upsertLink("icon", settings.favicon);
    upsertMeta("name", "robots", noindex ? "noindex, nofollow" : "index, follow");
    if (finalKeywords) upsertMeta("name", "keywords", finalKeywords);
    upsertLink("canonical", canonical);

    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:type", type);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", img);
    upsertMeta("property", "og:image:alt", title);
    upsertMeta("property", "og:site_name", SITE_NAME);
    upsertMeta("property", "og:locale", "en_GB");

    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", img);

    // Page-specific JSON-LD (Service / Article / etc., passed by each page).
    upsertJsonLd("seo-jsonld", jsonLd || null);
    // Site-wide LocalBusiness/Organization profile — on every page.
    upsertJsonLd("seo-jsonld-org", buildOrganizationLd());
    // Site-wide WebSite node (site name / publisher).
    upsertJsonLd("seo-jsonld-website", buildWebsiteLd());
    // Auto breadcrumb trail from the path (skipped on the home page). Use the
    // page title (minus the brand suffix) for the final crumb's label.
    const lastName = (finalTitle || "").split(/\s[|—–-]\s/)[0].trim() || undefined;
    upsertJsonLd("seo-jsonld-breadcrumb", buildBreadcrumbLd(path, lastName));
  }, [finalTitle, finalDescription, url, canonical, img, type, jsonLdStr, loaded, noindex, finalKeywords, path]);

  return null;
}

export { SITE_URL, SITE_NAME };
